import { z } from 'zod';
import { isoDateTimeSchema } from '../common/coercion.js';
import { flowIdSchema, systemSchema } from '../common/systems.js';
/**
 * Tablero de operacion del middleware.
 *
 * PARA QUIEN ES: para quien tiene que responder "como venimos" sin abrir la
 * tabla de mensajes. De ahi el orden de las secciones: primero si hay algo roto
 * AHORA, despues que tan confiable viene siendo, y al final el detalle por flujo
 * y por sistema.
 *
 * LO QUE ESTE TABLERO NO PUEDE DECIR: nada en pesos. El middleware mueve
 * mensajes, no conoce montos, margenes ni unidades vendidas —los flujos de
 * pedidos y ventas todavia no estan implementados—. Presentar "confiabilidad de
 * las integraciones" como si fuera impacto economico seria inventar; cuando F1 y
 * F5 corran, el dato existira y se agrega aca.
 */
/**
 * Ventana del tablero.
 *
 * Son tres y no un rango libre a proposito: un tablero que se reporta hacia
 * arriba tiene que dar el MISMO numero cada vez que dos personas lo abren. Con
 * rango libre, dos capturas de la misma semana no coinciden y la conversacion
 * pasa a ser sobre las fechas.
 */
export const dashboardPeriodSchema = z.enum(['24h', '7d', '30d']);
export const DASHBOARD_PERIODS = dashboardPeriodSchema.options;
/** Horas que cubre cada ventana. */
export const DASHBOARD_PERIOD_HOURS = Object.freeze({
    '24h': 24,
    '7d': 24 * 7,
    '30d': 24 * 30,
});
/** Filtro del tablero. */
export const dashboardQuerySchema = z.object({
    period: dashboardPeriodSchema.default('7d'),
});
/** Conteos de mensajes por desenlace. */
export const dashboardCountsSchema = z.object({
    total: z.number().int().nonnegative(),
    processed: z.number().int().nonnegative(),
    /** `failed` + `dead`: salio mal y ya no se reintenta solo. */
    failed: z.number().int().nonnegative(),
    quarantined: z.number().int().nonnegative(),
    /** Recibidos, encolados o procesandose: todavia no terminaron. */
    inFlight: z.number().int().nonnegative(),
});
/**
 * El titular: que tan confiable viene el periodo.
 *
 * `successRate` deja FUERA lo que sigue en vuelo, en numerador y denominador. Si
 * se contara lo no terminado como exito, el tablero mejoraria solo con
 * mensajes acumulandose sin procesar; si se contara como fallo, cada mirada a
 * media mañana mostraria una caida inventada.
 */
export const dashboardHealthSchema = z.object({
    counts: dashboardCountsSchema,
    /** Fraccion 0..1 sobre lo que SI termino. `null` cuando no termino nada. */
    successRate: z.number().min(0).max(1).nullable(),
    /** La misma cuenta en la ventana inmediatamente anterior, para comparar. */
    previousSuccessRate: z.number().min(0).max(1).nullable(),
    previousTotal: z.number().int().nonnegative(),
});
/**
 * Lo que exige que alguien haga algo.
 *
 * NO SE FILTRA POR PERIODO, a diferencia del resto del tablero: una dead letter
 * de hace dos meses sin resolver sigue siendo trabajo pendiente hoy, y filtrarla
 * por la ventana la esconderia justo cuando mas lleva esperando.
 */
export const dashboardBacklogSchema = z.object({
    deadLettersPending: z.number().int().nonnegative(),
    oldestDeadLetterAt: isoDateTimeSchema.nullable(),
    quarantinePending: z.number().int().nonnegative(),
    oldestQuarantineAt: isoDateTimeSchema.nullable(),
    /**
     * Mensajes que quedaron en vuelo mas tiempo del razonable.
     *
     * Es el numero que no aparece en ningun otro lado: un mensaje atascado no esta
     * fallado —nadie lo cuenta como error— pero tampoco avanza, y sin esto se
     * queda ahi hasta que alguien lo nota de casualidad.
     */
    stuck: z.number().int().nonnegative(),
    oldestStuckAt: isoDateTimeSchema.nullable(),
    /** A partir de cuantos minutos en vuelo se considera atascado. */
    stuckThresholdMinutes: z.number().int().positive(),
});
/** Cuanto tarda un mensaje desde que entra hasta que termina. */
export const dashboardLatencySchema = z.object({
    p50Ms: z.number().int().nonnegative(),
    p95Ms: z.number().int().nonnegative(),
    maxMs: z.number().int().nonnegative(),
});
/** Una fila del detalle por flujo o por sistema. */
export const dashboardBreakdownRowSchema = z.object({
    counts: dashboardCountsSchema,
    successRate: z.number().min(0).max(1).nullable(),
    lastActivityAt: isoDateTimeSchema.nullable(),
});
/** Detalle por flujo del plan. */
export const dashboardFlowRowSchema = dashboardBreakdownRowSchema.extend({
    /** `null` agrupa los mensajes que no declaran flujo. */
    flow: flowIdSchema.nullable(),
});
/** Detalle por sistema externo. */
export const dashboardSystemRowSchema = dashboardBreakdownRowSchema.extend({
    system: systemSchema,
});
/**
 * Un error que se repite.
 *
 * Se agrupa por el texto del error RECORTADO: los mensajes de error traen ids,
 * skus y timestamps, asi que agrupar por el texto completo devuelve mil grupos
 * de uno y no sirve para nada. El recorte junta las variantes del mismo
 * problema, a cambio de poder mezclar dos errores que empiezan igual.
 */
export const dashboardErrorRowSchema = z.object({
    /** Prefijo del error, ya recortado y normalizado. */
    message: z.string().min(1),
    count: z.number().int().positive(),
    system: systemSchema,
    flow: flowIdSchema.nullable(),
    lastSeenAt: isoDateTimeSchema,
    /** Un mensaje concreto con este error, para ir a ver la evidencia. */
    sampleMessageId: z.string().uuid(),
});
/** Un punto de la serie diaria. */
export const dashboardDayRowSchema = z.object({
    /** Dia en UTC, `YYYY-MM-DD`. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    total: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
/** El tablero completo. */
export const dashboardSummarySchema = z.object({
    period: dashboardPeriodSchema,
    from: isoDateTimeSchema,
    to: isoDateTimeSchema,
    health: dashboardHealthSchema,
    backlog: dashboardBacklogSchema,
    /** `null` cuando ningun mensaje del periodo llego a terminar. */
    latency: dashboardLatencySchema.nullable(),
    byFlow: z.array(dashboardFlowRowSchema),
    bySystem: z.array(dashboardSystemRowSchema),
    topErrors: z.array(dashboardErrorRowSchema),
    byDay: z.array(dashboardDayRowSchema),
});
/**
 * Tasa de exito sobre lo que termino.
 *
 * Vive en el contrato y no en cada punta para que la API y la UI no puedan
 * calcularla distinto: el mismo numero con dos definiciones es peor que no
 * tenerlo, porque nadie sabe cual de los dos esta mal.
 */
export function successRateOf(counts) {
    const finished = counts.processed + counts.failed + counts.quarantined;
    return finished === 0 ? null : counts.processed / finished;
}
//# sourceMappingURL=dashboard.js.map